import S from 'common/service/sanctuary';
import React from 'react';
import classnames from 'classnames';
import { node, bool, func, string, shape } from 'prop-types';

// eslint-disable-next-line import/no-extraneous-dependencies
import { MDCMenuFoundation, Corner } from '@material/menu/dist/mdc.menu';

export const ANCHOR_CLASS = 'mdc-menu-anchor';

class Menu extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      classes: [],
    };

    this.focus = null;
    this.handlers = {};
    this.hasAnchor = this.hasAnchor.bind(this);
    this.setPosition = this.setPosition.bind(this);
    this.getInnerDimensions = this.getInnerDimensions.bind(this);
    this.getDOMItems = this.getDOMItems.bind(this);
    this.getDOMItem = this.getDOMItem.bind(this);
    this.documentClickHandler = this.documentClickHandler.bind(this);
  }

  componentDidMount() {
    this.foundation = new MDCMenuFoundation({
      addClass: (className) => {
        this.setState(({ classes }) => ({
          classes: S.append(className, classes),
        }));
      },
      removeClass: (className) => {
        this.setState(({ classes }) => ({
          classes: S.filter(S.complement(S.equals(className)), classes),
        }));
      },
      hasClass: className => S.elem(className, [...this.root.classList]),
      hasNecessaryDom: () => Boolean(this.items),
      getAttributeForEventTarget: (target, attributeName) => target.getAttribute(attributeName),
      getInnerDimensions: this.getInnerDimensions,
      hasAnchor: this.hasAnchor,
      getAnchorDimensions: () => this.root.parentElement.getBoundingClientRect(),
      getWindowDimensions: () => ({ width: window.innerWidth, height: window.innerHeight }),
      getNumberOfItems: () => this.getDOMItems().length,
      registerInteractionHandler: (evtType, handler) => {
        this.handlers[evtType] = handler;
      },
      deregisterInteractionHandler: (evtType) => {
        delete this.handlers[evtType];
      },
      registerBodyClickHandler: () => {
        document.body.addEventListener('click', this.documentClickHandler);
      },
      deregisterBodyClickHandler: () => {
        document.body.removeEventListener('click', this.documentClickHandler);
      },
      getIndexForEventTarget: target => this.getDOMItems().indexOf(target),
      notifySelected: evtData => this.props.onSelect && this.props.onSelect(evtData),
      notifyCancel: () => {
        if (this.props.onCancel) {
          this.props.onCancel();
        }
      },
      saveFocus: () => {
        this.focus = document.activeElement;
      },
      restoreFocus: () => {
        if (!this.props.disableFocus && this.props.open && this.focus) {
          this.focus.focus();
        }
      },
      isFocused: () => document.activeElement === this.root,
      focus: () => !this.props.disableFocus && this.props.open && this.root.focus(),
      getFocusedItemIndex: () => this.getDOMItems().indexOf(document.activeElement),
      focusItemAtIndex: index => !this.props.disableFocus
         && this.props.open
         && S.maybeToNullable(S.map(item => item.focus(), this.getDOMItem(index))),
      isRtl: () => getComputedStyle(this.root).getPropertyValue('direction') === 'rtl',
      setTransformOrigin: origin => this.root.style.setProperty('transform-origin', origin),
      setPosition: this.setPosition,
      setMaxHeight: (height) => {
        const maxHeight = S.min(
          parseInt(height, 10),
          parseInt(this.props.style.maxHeight, 10) || Infinity,
        );

        this.root.style.maxHeight = `${maxHeight}px`;
      },
    });

    this.foundation.init();
    if (!this.props.overlay) {
      this.foundation.setAnchorCorner(Corner.BOTTOM_START);
    }

    if (this.props.open) {
      this.foundation.open();
    }
  }

  componentWillReceiveProps({ open }) {
    if (open && !this.props.open) {
      this.foundation.open();
    }
    if (!open && this.props.open) {
      this.foundation.close();
    }
  }

  shouldComponentUpdate(props, state) {
    return !S.equals(this.props, props) || !S.equals(this.state, state);
  }

  componentWillUnmount() {
    this.foundation.destroy();
  }

  getInnerDimensions() {
    const { items } = this;
    return { width: items.offsetWidth, height: items.offsetHeight };
  }

  getDOMItems() {
    const { items } = this;
    if (!items) {
      return [];
    }

    return [].slice.call(items.querySelectorAll('.mdc-list-item[role]'));
  }

  getDOMItem(idx) {
    return S.toMaybe(this.getDOMItems()[idx]);
  }

  setPosition(position) {
    this.root.style.left = 'left' in position ? position.left : null;
    this.root.style.right = 'right' in position ? position.right : null;
    this.root.style.top = 'top' in position ? position.top : null;
    this.root.style.bottom = 'bottom' in position ? position.bottom : null;
  }

  // This is and override of mdc document click handler,
  // as it behaves incorrectly menu children items clicks
  documentClickHandler(evt) {
    let el = evt.target;
    const floatingMenuContainer = document.querySelector('.menus');

    while (el && el !== document.body) {
      if (el === floatingMenuContainer) {
        return;
      }

      if (el === this.items) {
        return;
      }

      el = el.parentNode;
    }

    if (this.props.onCancel) {
      this.props.onCancel();
    }

    this.foundation.close();
  }

  hasAnchor() {
    return this.root.parentElement
      && this.root.parentElement.classList.contains(ANCHOR_CLASS);
  }

  render() {
    const {
      children,
      className,
      menuRef,
      style,
      id,
    } = this.props;

    const { classes } = this.state;

    return (
      <div
        ref={(menu) => { this.root = menu; menuRef(menu); }}
        className={classnames(
          'mdc-menu',
          ...classes,
          className,
        )}
        tabIndex="-1"
        style={style}
        id={id}
        role="button"
        onClick={this.handlers.click}
        onKeyDown={this.handlers.keydown}
        onKeyUp={this.handlers.keyup}
      >
        <ul
          ref={(items) => { this.items = items; }}
          className="mdc-menu__items mdc-list"
          role="menu"
          aria-hidden="true"
        >
          {children}
        </ul>
      </div>
    );
  }
}

Menu.propTypes = {
  children: node.isRequired,
  onSelect: func,
  onCancel: func,
  menuRef: func,
  className: string,
  open: bool,
  overlay: bool,
  style: shape(),
  disableFocus: bool,
  id: string,
};

Menu.defaultProps = {
  className: null,
  open: false,
  disableFocus: false,
  onSelect: null,
  onCancel: null,
  overlay: true,
  style: {},
  menuRef: () => null,
  id: undefined,
};

export default Menu;
